import type { CCPreset } from '~/types/presets';
import type { MidiCC, DeviceProfile } from '~/types/midi';

/**
 * "OG" CC preset for YO! FM Synth
 *
 * This preserves the original default values from src/config/defaults/fmsynth.json
 * before the Init preset adjustments were made.
 */

const ogFMSynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 90, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 14, value: 104, scramble: true, smooth: false, show: true, min: 20, max: 127 },
  { number: 15, value: 110, scramble: true, smooth: false, show: true, min: 10, max: 120 },
  { number: 16, value: 99, scramble: true, smooth: false, show: true, min: 0, max: 100 },
  { number: 17, value: 44, scramble: true, smooth: false, show: true, min: 5, max: 127 },
  { number: 18, value: 50, scramble: true, smooth: false, show: true, min: 20, max: 127 },
  { number: 19, value: 62, scramble: true, smooth: false, show: true, min: 5, max: 127 },
  { number: 70, value: 90, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 71, value: 35, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 72, value: 85, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 73, value: 47, scramble: true, smooth: false, show: true, min: 3, max: 80 },
  { number: 74, value: 80, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 75, value: 18, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 76, value: 7, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 77, value: 64, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 78, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
];

/**
 * Device profile for "OG" FM Synth preset
 */
const ogFMSynthProfile: DeviceProfile = {
  id: 'yo-fm-synth-og',
  name: 'OG',
  deviceName: 'YO! FM Synth',
  userId: null, // System profile
  isPublic: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ccConfig: [
    {
      number: 1,
      enabled: false,
      min: 0,
      max: 127,
      label: 'Modulation',
    },
    {
      number: 7,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Volume',
    },
    {
      number: 11,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Expression',
    },
    {
      number: 14,
      enabled: true,
      min: 20,
      max: 127,
      label: 'Modulation Index',
    },
    {
      number: 15,
      enabled: true,
      min: 10,
      max: 120,
      label: 'Harmonicity',
    },
    {
      number: 16,
      enabled: false,
      min: 0,
      max: 100,
      label: 'Mod Env Attack',
    },
    {
      number: 17,
      enabled: true,
      min: 5,
      max: 127,
      label: 'Mod Env Decay',
    },
    {
      number: 18,
      enabled: true,
      min: 20,
      max: 127,
      label: 'Mod Env Sustain',
    },
    {
      number: 19,
      enabled: true,
      min: 5,
      max: 127,
      label: 'Mod Env Release',
    },
    {
      number: 70,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Carrier Sustain',
    },
    {
      number: 72,
      enabled: false,
      min: 10,
      max: 127,
      label: 'Carrier Release',
    },
    {
      number: 73,
      enabled: false,
      min: 3,
      max: 80,
      label: 'Carrier Attack',
    },
    {
      number: 74,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Modulator Type',
    },
    {
      number: 75,
      enabled: true,
      min: 10,
      max: 127,
      label: 'Carrier Decay',
    },
    {
      number: 76,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Carrier Type',
    },
  ],
};

/**
 * "OG" FM Synth CC Preset
 */
export const OG_FM_SYNTH_PRESET: CCPreset = {
  id: 'og-fm',
  name: 'OG',
  deviceType: 'FMSynth',
  cc: ogFMSynthCCs,
  profiles: [ogFMSynthProfile],
  activeProfileId: 'yo-fm-synth-og',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "OG" FM Synth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getOGFMSynthPreset(): CCPreset {
  return {
    ...OG_FM_SYNTH_PRESET,
    cc: OG_FM_SYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: OG_FM_SYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
